/** @format */

import React, { useState, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
    Box,
    Chip,
    Container,
    Typography,
    Button,
    useTheme,
    useMediaQuery,
    alpha,
} from "@mui/material";
import { Public, East } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
    SUPPORTED_COUNTRIES,
    CountryConfig,
} from "../../utility/stripeConnectCountries";
import { colors } from "../../utility/types/constant";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const countryCodeToEmoji = (code: string): string =>
    code
        .toUpperCase()
        .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

const REGIONS: Record<string, string[]> = {
    "North America": ["US", "CA", "MX"],
    Europe: [
        "GB", "DE", "FR", "ES", "IT", "NL", "BE", "IE", "PT", "AT", "CH", "SE", "NO", "DK",
        "FI", "PL", "CZ", "HU", "RO", "BG", "HR", "SK", "SI", "EE", "LV", "LT", "CY", "MT",
        "LU", "GR", "LI", "GI", "AL", "BA", "IS", "MC", "MD", "MK", "RS", "AM", "TR", "IL",
    ],
    "Asia Pacific": [
        "AU", "NZ", "SG", "HK", "JP", "MY", "TH", "ID", "PH", "VN", "IN", "KR", "BN", "KH",
        "MO", "TW", "MN", "LK", "PK", "UZ",
    ],
    "Latin America": [
        "BR", "AR", "CL", "CO", "PE", "UY", "BO", "CR", "DO", "EC", "SV", "GT", "GY", "PA", "PY",
    ],
    Caribbean: ["TT", "JM", "AG", "BS", "LC"],
    "Middle East": ["AE", "BH", "SA", "QA", "KW", "OM", "JO"],
    Africa: [
        "ZA", "NG", "GH", "KE", "CI", "SN", "EG", "MA", "TZ", "RW", "ET", "BJ", "BW", "GM",
        "MG", "MU", "NA", "TN",
    ],
};

const REGION_ORDER = [
    "All",
    "North America",
    "Europe",
    "Asia Pacific",
    "Latin America",
    "Caribbean",
    "Middle East",
    "Africa",
];

const getRegion = (code: string): string => {
    for (const [region, codes] of Object.entries(REGIONS)) {
        if (codes.includes(code)) return region;
    }
    return "Other";
};

const uniqueCurrencies = new Set(
    SUPPORTED_COUNTRIES.map((c: CountryConfig) => c.currency.toUpperCase()),
);

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatPill: React.FC<{ value: string; label: string }> = ({ value, label }) => (
    <Box
        sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            px: { xs: 2.5, md: 4 },
            py: 1.5,
            borderRadius: 3,
            border: `1px solid ${alpha(colors.mainSecondary, 0.3)}`,
            bgcolor: alpha(colors.mainSecondary, 0.07),
        }}
    >
        <Typography
            sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 800,
                fontSize: { xs: "1.6rem", md: "2rem" },
                color: colors.mainSecondary,
                lineHeight: 1,
            }}
        >
            {value}
        </Typography>
        <Typography
            variant="caption"
            sx={{ color: alpha("#fff", 0.65), mt: 0.5, letterSpacing: 0.5 }}
        >
            {label}
        </Typography>
    </Box>
);

interface CountryCardProps {
    country: CountryConfig;
    index: number;
}

const CountryCard: React.FC<CountryCardProps> = ({ country, index }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "0px 0px -40px 0px" });

    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 18, scale: 0.95 }}
            animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{ duration: 0.35, delay: (index % 20) * 0.03, ease: "easeOut" }}
            style={{ minWidth: 0, width: "100%" }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: { xs: 1, sm: 1.8 },
                    py: { xs: 1, sm: 1.2 },
                    borderRadius: 2.5,
                    border: `1px solid ${alpha("#fff", 0.08)}`,
                    bgcolor: alpha("#fff", 0.04),
                    cursor: "default",
                    minWidth: 0,
                    width: "100%",
                    overflow: "hidden",
                    transition: "all 0.22s ease",
                    "&:hover": {
                        bgcolor: alpha(colors.mainSecondary, 0.12),
                        border: `1px solid ${alpha(colors.mainSecondary, 0.4)}`,
                        transform: "translateY(-2px)",
                        boxShadow: `0 4px 16px ${alpha(colors.mainSecondary, 0.15)}`,
                    },
                }}
            >
                <Typography sx={{ fontSize: { xs: "1.1rem", sm: "1.35rem" }, lineHeight: 1, flexShrink: 0 }}>
                    {countryCodeToEmoji(country.code)}
                </Typography>
                <Typography
                    sx={{
                        color: alpha("#fff", 0.9),
                        fontSize: { xs: "0.7rem", sm: "0.78rem" },
                        fontWeight: 500,
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        minWidth: 0,
                        flex: 1,
                    }}
                >
                    {country.name}
                </Typography>
            </Box>
        </motion.div>
    );
};

// ─── Infinite marquee ─────────────────────────────────────────────────────────

const FlagMarquee: React.FC = () => {
    const flags = SUPPORTED_COUNTRIES.map((c) => ({
        code: c.code,
        emoji: countryCodeToEmoji(c.code),
        name: c.name,
    }));
    const doubled = [...flags, ...flags];

    return (
        <Box
            sx={{
                overflow: "hidden",
                width: "100%",
                py: 1.5,
                maskImage:
                    "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
                WebkitMaskImage:
                    "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                    width: "max-content",
                    animation: "marqueeScroll 60s linear infinite",
                    "@keyframes marqueeScroll": {
                        "0%": { transform: "translateX(0)" },
                        "100%": { transform: `translateX(-50%)` },
                    },
                    "&:hover": {
                        animationPlayState: "paused",
                    },
                }}
            >
                {doubled.map((f, i) => (
                    <Box
                        key={`${f.code}-${i}`}
                        title={f.name}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.8,
                            px: 1.5,
                            py: 0.6,
                            borderRadius: 2,
                            bgcolor: alpha("#fff", 0.05),
                            border: `1px solid ${alpha("#fff", 0.07)}`,
                            flexShrink: 0,
                            transition: "all 0.2s",
                            "&:hover": {
                                bgcolor: alpha(colors.mainSecondary, 0.15),
                                border: `1px solid ${alpha(colors.mainSecondary, 0.4)}`,
                            },
                        }}
                    >
                        <Typography sx={{ fontSize: "1.1rem", lineHeight: 1 }}>{f.emoji}</Typography>
                        <Typography
                            sx={{ fontSize: "0.72rem", color: alpha("#fff", 0.7), fontWeight: 500 }}
                        >
                            {f.code}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const SupportedCountriesSection: React.FC = () => {
    const { t } = useTranslation("home");
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const navigate = useNavigate();
    const [activeRegion, setActiveRegion] = useState<string | null>(null);
    const headerRef = useRef(null);
    const headerInView = useInView(headerRef, { once: true, margin: "0px 0px -60px 0px" });

    // Map internal region key → translated label
    const regionLabels: Record<string, string> = {
        "North America": t("supportedCountriesSection.regionNorthAmerica"),
        Europe: t("supportedCountriesSection.regionEurope"),
        "Asia Pacific": t("supportedCountriesSection.regionAsiaPacific"),
        "Latin America": t("supportedCountriesSection.regionLatinAmerica"),
        Caribbean: t("supportedCountriesSection.regionCaribbean"),
        "Middle East": t("supportedCountriesSection.regionMiddleEast"),
        Africa: t("supportedCountriesSection.regionAfrica"),
    };

    const filtered = activeRegion
        ? SUPPORTED_COUNTRIES.filter((c) => getRegion(c.code) === activeRegion)
        : [];

    return (
        <Box
            component="section"
            sx={{
                bgcolor: colors.primary,
                py: { xs: 7, md: 10 },
                position: "relative",
                overflow: "hidden",
                width: "100%",
                maxWidth: "100vw",
            }}
        >
            {/* Background decorative blobs */}
            <Box
                sx={{
                    position: "absolute",
                    top: -120,
                    right: -120,
                    width: 500,
                    height: 500,
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${alpha(colors.mainSecondary, 0.08)} 0%, transparent 70%)`,
                    pointerEvents: "none",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    bottom: -100,
                    left: -100,
                    width: 400,
                    height: 400,
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${alpha(colors.mainSecondary, 0.06)} 0%, transparent 70%)`,
                    pointerEvents: "none",
                }}
            />

            <Container maxWidth="xl">
                {/* ── Header ── */}
                <motion.div
                    ref={headerRef}
                    initial={{ opacity: 0, y: 30 }}
                    animate={headerInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.55, ease: "easeOut" }}
                >
                    <Box sx={{ textAlign: "center", mb: 5 }}>
                        <Chip
                            icon={<Public sx={{ color: `${colors.mainSecondary} !important`, fontSize: "0.95rem" }} />}
                            label={t("supportedCountriesSection.badge")}
                            size="small"
                            sx={{
                                mb: 2,
                                bgcolor: alpha(colors.mainSecondary, 0.1),
                                color: colors.mainSecondary,
                                border: `1px solid ${alpha(colors.mainSecondary, 0.3)}`,
                                fontWeight: 600,
                                fontSize: "0.72rem",
                                letterSpacing: 1,
                                textTransform: "uppercase",
                            }}
                        />
                        <Typography
                            variant="h3"
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 800,
                                color: "#fff",
                                fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.8rem" },
                                lineHeight: 1.15,
                                mb: 1.5,
                            }}
                        >
                            {t("supportedCountriesSection.title")}{" "}
                            <Box component="span" sx={{ color: colors.mainSecondary }}>
                                {t("supportedCountriesSection.titleHighlight", { count: SUPPORTED_COUNTRIES.length })}
                            </Box>
                            {" "}{t("supportedCountriesSection.titleSuffix")}
                        </Typography>
                        <Typography
                            sx={{
                                color: alpha("#fff", 0.6),
                                fontSize: { xs: "0.9rem", md: "1rem" },
                                maxWidth: 560,
                                mx: "auto",
                                lineHeight: 1.7,
                            }}
                        >
                            {t("supportedCountriesSection.subtitle")}
                        </Typography>

                        {/* Stats strip */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                flexWrap: "wrap",
                                gap: 2,
                                mt: 4,
                            }}
                        >
                            <StatPill value={`${SUPPORTED_COUNTRIES.length}+`} label={t("supportedCountriesSection.statCountries")} />
                            <StatPill value={`${Object.keys(REGIONS).length}`} label={t("supportedCountriesSection.statRegions")} />
                            <StatPill value={`${uniqueCurrencies.size}+`} label={t("supportedCountriesSection.statCurrencies")} />
                            <StatPill value="100%" label={t("supportedCountriesSection.statStripe")} />
                        </Box>
                    </Box>
                </motion.div>

                {/* ── Marquee ── */}
                <Box sx={{ mb: 5 }}>
                    <FlagMarquee />
                </Box>

                {/* ── Region Filter ── */}
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1.5,
                        justifyContent: "center",
                        mb: 3,
                    }}
                >
                    {REGION_ORDER.filter((r) => r !== "All").map((region) => {
                        const count = SUPPORTED_COUNTRIES.filter((c) => getRegion(c.code) === region).length;
                        const isActive = activeRegion === region;
                        return (
                            <Chip
                                key={region}
                                label={`${regionLabels[region] ?? region} (${count})`}
                                onClick={() => setActiveRegion(isActive ? null : region)}
                                sx={{
                                    fontWeight: isActive ? 700 : 500,
                                    fontSize: { xs: "0.72rem", sm: "0.78rem" },
                                    py: { xs: 0.5, sm: 1 },
                                    height: "auto",
                                    bgcolor: isActive ? colors.mainSecondary : alpha("#fff", 0.06),
                                    color: isActive ? colors.primary : alpha("#fff", 0.75),
                                    border: `1px solid ${isActive ? colors.mainSecondary : alpha("#fff", 0.12)}`,
                                    transition: "all 0.2s ease",
                                    cursor: "pointer",
                                    "&:hover": {
                                        bgcolor: isActive
                                            ? colors.mainSecondary
                                            : alpha(colors.mainSecondary, 0.15),
                                        color: isActive ? colors.primary : colors.mainSecondary,
                                        border: `1px solid ${alpha(colors.mainSecondary, 0.5)}`,
                                    },
                                }}
                            />
                        );
                    })}
                </Box>

                {/* ── Hint when nothing selected ── */}
                <AnimatePresence>
                    {!activeRegion && (
                        <motion.div
                            key="hint"
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.25 }}
                        >
                            <Typography
                                sx={{
                                    textAlign: "center",
                                    color: alpha("#fff", 0.35),
                                    fontSize: "0.82rem",
                                    mb: 4,
                                    letterSpacing: 0.3,
                                }}
                            >
                                ↑ Select a region above to explore supported countries
                            </Typography>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ── Country Grid ── */}
                <AnimatePresence mode="wait">
                    {activeRegion && (
                        <motion.div
                            key={activeRegion}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.25 }}
                        >
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "repeat(2, minmax(0, 1fr))",
                                        sm: "repeat(3, minmax(0, 1fr))",
                                        md: "repeat(4, minmax(0, 1fr))",
                                        lg: "repeat(5, minmax(0, 1fr))",
                                        xl: "repeat(6, minmax(0, 1fr))",
                                    },
                                    gap: { xs: 0.8, sm: 1.2 },
                                    width: "100%",
                                    minWidth: 0,
                                    mb: 2,
                                }}
                            >
                                {filtered.map((country, i) => (
                                    <CountryCard key={country.code} country={country} index={i} />
                                ))}
                            </Box>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ── CTA ── */}
                <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
                    <Button
                        variant="outlined"
                        endIcon={<East />}
                        onClick={() => navigate("/supported-countries")}
                        sx={{
                            borderColor: colors.mainSecondary,
                            color: colors.mainSecondary,
                            fontWeight: 700,
                            px: 4,
                            py: 1.3,
                            borderRadius: 3,
                            textTransform: "none",
                            fontSize: "0.9rem",
                            letterSpacing: 0.3,
                            transition: "all 0.25s ease",
                            "&:hover": {
                                bgcolor: colors.mainSecondary,
                                color: colors.primary,
                                borderColor: colors.mainSecondary,
                                transform: "translateY(-2px)",
                                boxShadow: `0 6px 20px ${alpha(colors.mainSecondary, 0.35)}`,
                            },
                        }}
                    >
                        {t("supportedCountriesSection.cta")}
                    </Button>
                </Box>
            </Container>
        </Box>
    );
};

export default SupportedCountriesSection;
